import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Paperclip,
  ImagePlus,
  CheckCircle,
  DollarSign,
} from "lucide-react";

interface ExampleReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SampleMessage {
  id: string;
  senderName: string;
  isOwn: boolean;
  content: string;
  timestamp: string;
}

// picsum.photos returns real photos keyed by seed — stable across renders and
// good enough to look like real job-site photos in the walkthrough.
const SAMPLE_ATTACHMENTS: { name: string; url: string }[] = [
  { name: "basement-damage.jpg", url: "https://picsum.photos/seed/basement-damage/400/400" },
  { name: "wall-damage.jpg", url: "https://picsum.photos/seed/wall-damage/400/400" },
  { name: "moisture-reading.jpg", url: "https://picsum.photos/seed/moisture-reading/400/400" },
  { name: "signed-estimate.jpg", url: "https://picsum.photos/seed/signed-estimate/400/400" },
];

const SAMPLE_MESSAGES: SampleMessage[] = [
  {
    id: "m1",
    senderName: "Alex Demo",
    isOwn: false,
    content: "Got the referral, thanks! I can be at the address Wednesday morning.",
    timestamp: "May 14, 10:32 AM",
  },
  {
    id: "m2",
    senderName: "You",
    isOwn: true,
    content: "Perfect. I told the homeowner to expect you between 9 and 10.",
    timestamp: "May 14, 10:41 AM",
  },
  {
    id: "m3",
    senderName: "Alex Demo",
    isOwn: false,
    content: "Onsite now. Confirmed water damage in the basement — pulling up carpet and setting fans. Uploading photos in a sec.",
    timestamp: "May 15, 9:18 AM",
  },
  {
    id: "m4",
    senderName: "Alex Demo",
    isOwn: false,
    content: "Estimate is $8,400 including mitigation and drywall repair. Homeowner signed.",
    timestamp: "May 15, 2:04 PM",
  },
  {
    id: "m5",
    senderName: "You",
    isOwn: true,
    content: "Awesome, thanks Alex — let me know when the job wraps.",
    timestamp: "May 15, 2:12 PM",
  },
  {
    id: "m6",
    senderName: "Alex Demo",
    isOwn: false,
    content: "Job complete. Customer's happy. Marking it done and closing out.",
    timestamp: "May 21, 4:30 PM",
  },
];

export default function ExampleReferralModal({
  isOpen,
  onClose,
}: ExampleReferralModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl p-0 overflow-hidden gap-0 max-h-[90vh] flex flex-col">
        {/* ── Header (fixed) ── */}
        <div className="bg-green-50 border-b border-green-200 px-5 py-4 flex-shrink-0">
          <DialogHeader className="text-left space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-green-100 text-green-800 border border-green-200 text-xs">
                EXAMPLE REFERRAL
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs">
                Completed
              </Badge>
              <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs">
                Sent
              </Badge>
              <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-xs">
                Normal
              </Badge>
              <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-xs">
                Restoration
              </Badge>
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900 text-center pt-1">
              Jordan Sample
            </DialogTitle>
            <p className="text-sm text-gray-500 text-center">
              May 14, 2025 at 10:20 AM
            </p>
            <div className="bg-white/70 rounded-md px-3 py-2 text-xs text-gray-700 border border-green-200 text-center">
              Practice only. No one will be contacted.
            </div>
          </DialogHeader>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 bg-white">
          <div className="p-5 space-y-5">
            {/* Status & Date + Referred To */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                  Status &amp; Date
                </p>
                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 mb-2">
                  Completed
                </Badge>
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  May 14, 2025
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                  Referred To
                </p>
                <div className="flex items-center gap-2">
                  <Avatar className="w-10 h-10 border border-gray-200 flex-shrink-0">
                    <AvatarFallback className="bg-blue-50 text-blue-600 text-sm font-medium">
                      AD
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      Alex Demo
                    </p>
                    <p className="text-xs text-gray-500 truncate">Restoration</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                Contact Information
              </p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                <div className="flex items-center px-4 py-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">Name</p>
                    <p className="text-sm font-semibold text-gray-900">Jordan Sample</p>
                  </div>
                </div>
                <div className="flex items-center px-4 py-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">Phone</p>
                    <p className="text-sm font-semibold text-gray-900">(208) 555-0142</p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-semibold text-green-700 bg-transparent px-3 py-1.5 rounded-lg border border-gray-300 opacity-60">
                    Call
                  </span>
                </div>
                <div className="flex items-center px-4 py-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      jordan@example.com
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-semibold text-pink-500 bg-transparent px-3 py-1.5 rounded-lg border border-gray-300 opacity-60">
                    Email
                  </span>
                </div>
                <div className="flex items-center px-4 py-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">Location</p>
                    <p className="text-sm font-semibold text-gray-900">
                      123 Example Lane, Boise, ID — fictional
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-semibold text-orange-500 bg-transparent px-3 py-1.5 rounded-lg border border-gray-300 opacity-60">
                    Map
                  </span>
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                Job Description
              </p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                  Water damage in basement following a pipe burst overnight. Standing water in the
                  utility room and finished basement. Homeowner has already turned off the main and
                  is available weekday mornings. Needs mitigation and drywall repair. Insurance
                  claim already opened with State Farm.
                </p>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                Additional Notes
              </p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 border-l-4 border-l-amber-400 p-4">
                <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                  Homeowner mentioned the sump pump is 12 years old and never serviced. Might be
                  worth a look while you're onsite. She's flexible on scheduling — prefers phone
                  over text.
                </p>
              </div>
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                  Attachments
                </p>
                <p className="text-xs text-gray-400">Shared with both parties</p>
              </div>
              <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                {SAMPLE_ATTACHMENTS.map((attachment) => (
                  <div
                    key={attachment.name}
                    className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200"
                  >
                    <img
                      src={attachment.url}
                      alt={attachment.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fall back to a plain paperclip if the placeholder host is offline.
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1.5 py-1">
                      <p className="text-white text-[10px] truncate">{attachment.name}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="w-full bg-gray-50 border border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-400">
                <ImagePlus className="w-4 h-4" />
                <span className="text-sm">Add photos or videos</span>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Both partners can add files. Max 20 files (4/20).
              </p>
            </div>

            {/* Messages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                  Messages
                </p>
                <p className="text-xs text-gray-400">{SAMPLE_MESSAGES.length} messages</p>
              </div>
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-y-auto max-h-[300px] p-3 space-y-2">
                  {SAMPLE_MESSAGES.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] ${
                          msg.isOwn ? "items-end" : "items-start"
                        } flex flex-col`}
                      >
                        {!msg.isOwn && (
                          <p className="text-[10px] font-medium text-gray-500 mb-0.5 px-1">
                            {msg.senderName}
                          </p>
                        )}
                        <div
                          className={`rounded-2xl px-3 py-1.5 text-sm ${
                            msg.isOwn
                              ? "bg-blue-600 text-white rounded-br-sm"
                              : "bg-gray-200 text-gray-900 rounded-bl-sm"
                          }`}
                        >
                          {msg.content}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5 px-1">
                          {msg.timestamp}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 bg-white px-3 py-2 flex items-center gap-2">
                  <div className="flex-1 text-sm text-gray-400 border border-gray-200 rounded-full px-3 py-1.5">
                    Type a message…
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                    <Paperclip className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>

            {/* Completion */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                Completion
              </p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-green-800 font-semibold text-sm">
                  <CheckCircle className="w-5 h-5" />
                  Job completed &amp; customer satisfied
                </div>
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <Calendar className="w-4 h-4" />
                  Closed May 21, 2025
                </div>
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <DollarSign className="w-4 h-4" />
                  Job value: $8,400.00
                </div>
                <p className="text-sm text-green-900 leading-relaxed pt-1 border-t border-green-200">
                  Mitigation completed in 3 days. Drywall repair and paint finished on day 6.
                  Homeowner left a 5-star review and asked for our card for a future kitchen job.
                </p>
              </div>
            </div>

            {/* Attribution */}
            <p className="text-xs text-gray-400 text-center pt-2">
              This is a sample referral used for the Getting Started walkthrough. All contact
              information, photos, and messages are fictional.
            </p>
          </div>
        </div>

        {/* ── Footer (fixed) ── */}
        <div className="border-t border-gray-200 bg-white px-5 py-3 flex items-center justify-end flex-shrink-0">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
